import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { HomeModel } from '@models/home.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { UiButtonComponent, UiDeviceListComponent } from '@ui';

import { UiCameraStatusSnapshotComponent } from '../../../../ui-kit/ui-camera-status-snapshot/ui-camera-status-snapshot.component';
import { BulkUpdateFormComponent } from 'src/app/pages/location-v2/pages/device/pages/device-list/components/bulk-update-form-modal/bulk-update-form.component';
import { ChangePasswordDialogComponent } from 'src/app/pages/location-v2/pages/device/pages/device-list/components/change-password-dialog/change-password-dialog.component';
import {
  TestSelectedCameraModalComponent,
  TestSelectedCameraModalData,
} from 'src/app/pages/location-v2/pages/device/pages/device-list/components/test-selected-camera-modal/test-selected-camera-modal.component';

type Device = IntelligenceModels.DeviceList.Device;
type DeviceLocation = IntelligenceModels.DeviceList.Location;

type DeviceListRow =
  | { id: string; type: 'location'; location: DeviceLocation; depth: number }
  | {
      id: string;
      type: 'section-header';
      location: DeviceLocation;
      depth: number;
    }
  | {
      id: string;
      type: 'device';
      location: DeviceLocation;
      device: Device;
      depth: number;
      last: boolean;
    };

@Component({
  selector: 'app-device-list-result',
  standalone: true,
  imports: [
    CommonModule,
    MatCheckbox,
    TranslatePipe,
    UiButtonComponent,
    UiCameraStatusSnapshotComponent,
    UiDeviceListComponent,
  ],
  templateUrl: './device-list-result.component.html',
  styleUrl: './device-list-result.component.scss',
})
export class DeviceListResultComponent {
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  public readonly response = input<IntelligenceModels.DeviceList.Response | null>(
    null,
  );
  public readonly expandedLocationIds = signal<ReadonlySet<string>>(new Set());
  public readonly selectedCameraIds = signal<ReadonlySet<string>>(new Set());
  public readonly selectedCount = computed(() => this.selectedCameraIds().size);
  public readonly enableChangePasswordFeature = toSignal(
    this.store.select(
      OrganizationSelectors.selectEnableChangePasswordFeature,
    ),
    { initialValue: false },
  );

  private readonly locations = computed(() => this.response()?.locations ?? []);

  public readonly rows = computed<DeviceListRow[]>(() => {
    const rows: DeviceListRow[] = [];
    const expanded = this.expandedLocationIds();

    const appendLocation = (location: DeviceLocation, depth: number): void => {
      rows.push({
        id: `location:${location.id}`,
        type: 'location',
        location,
        depth,
      });
      if (!expanded.has(location.id)) return;

      for (const subLocation of location.subLocations) {
        appendLocation(subLocation, depth + 1);
      }
      if (!location.devices.length) return;

      rows.push({
        id: `section:${location.id}`,
        type: 'section-header',
        location,
        depth: depth + 1,
      });
      location.devices.forEach((device, index) => {
        rows.push({
          id: `device:${location.id}:${device.type}:${device.id}`,
          type: 'device',
          location,
          device,
          depth: depth + 1,
          last: index === location.devices.length - 1,
        });
      });
    };

    for (const location of this.locations()) appendLocation(location, 0);
    return rows;
  });

  public readonly selectedCameras = computed<HomeModel.Camera[]>(() => {
    const selected = this.selectedCameraIds();
    return this.allDevices()
      .filter(
        (device) =>
          device.type === IntelligenceModels.DeviceList.DeviceType.Camera &&
          selected.has(device.id) &&
          !!device.edgeId,
      )
      .map((device) => ({
        cameraId: device.id,
        edgeId: device.edgeId!,
        locationId: device.locationId,
        name: device.name,
      }));
  });

  public readonly rowKey = (row: DeviceListRow): string => row.id;
  public readonly estimateRowSize = (
    row: DeviceListRow,
    index: number,
  ): number => {
    if (row.type === 'location') {
      if (row.depth > 0) return 54;
      return index === 0 ? 49 : 55;
    }
    return row.type === 'section-header' ? 52 : row.last ? 69 : 52;
  };

  public locationPaddingTop(
    row: Extract<DeviceListRow, { type: 'location' }>,
    index: number,
  ): number {
    if (index === 0) return 0;
    return row.depth === 0 ? 8 : 6;
  }

  public toggleLocation(location: DeviceLocation): void {
    if (!this.locationDeviceCount(location)) return;
    this.expandedLocationIds.update((current) => {
      const next = new Set(current);
      if (next.has(location.id)) next.delete(location.id);
      else next.add(location.id);
      return next;
    });
  }

  public isExpanded(locationId: string): boolean {
    return this.expandedLocationIds().has(locationId);
  }

  public locationDeviceCount(location: DeviceLocation): number {
    return (
      location.devices.length +
      location.subLocations.reduce(
        (count, subLocation) => count + this.locationDeviceCount(subLocation),
        0,
      )
    );
  }

  public statusCount(
    location: DeviceLocation,
    status: 'online' | 'unhealthy' | 'offline',
  ): number {
    return this.locationDevices(location).filter(
      (device) => device.status === status,
    ).length;
  }

  public locationCameras(location: DeviceLocation): Device[] {
    return this.locationDevices(location).filter(
      (device) =>
        device.type === IntelligenceModels.DeviceList.DeviceType.Camera,
    );
  }

  public isLocationSelected(location: DeviceLocation): boolean {
    const cameras = this.locationCameras(location);
    return (
      cameras.length > 0 &&
      cameras.every((camera) => this.selectedCameraIds().has(camera.id))
    );
  }

  public isLocationIndeterminate(location: DeviceLocation): boolean {
    const cameras = this.locationCameras(location);
    const selectedCount = cameras.filter((camera) =>
      this.selectedCameraIds().has(camera.id),
    ).length;
    return selectedCount > 0 && selectedCount < cameras.length;
  }

  public selectLocation(
    location: DeviceLocation,
    change: MatCheckboxChange,
  ): void {
    this.updateCameraSelection(
      this.locationCameras(location).map((camera) => camera.id),
      change.checked,
    );
  }

  public selectCamera(cameraId: string, change: MatCheckboxChange): void {
    this.updateCameraSelection([cameraId], change.checked);
  }

  public isCameraSelected(cameraId: string): boolean {
    return this.selectedCameraIds().has(cameraId);
  }

  public selectAllCameras(): void {
    this.selectedCameraIds.set(
      new Set(
        this.allDevices()
          .filter(
            (device) =>
              device.type === IntelligenceModels.DeviceList.DeviceType.Camera,
          )
          .map((device) => device.id),
      ),
    );
  }

  public clearSelectedCameras(): void {
    this.selectedCameraIds.set(new Set());
  }

  public deviceTypeLabel(device: Device): string {
    const labels: Record<IntelligenceModels.DeviceList.DeviceType, string> = {
      [IntelligenceModels.DeviceList.DeviceType.Camera]: 'Camera',
      [IntelligenceModels.DeviceList.DeviceType.Core]: 'Core',
      [IntelligenceModels.DeviceList.DeviceType.AppleTv]: 'Apple TV',
      [IntelligenceModels.DeviceList.DeviceType.Door]: 'Door',
      [IntelligenceModels.DeviceList.DeviceType.Speaker]: 'Speaker',
      [IntelligenceModels.DeviceList.DeviceType.Sensor]: 'Sensor',
      [IntelligenceModels.DeviceList.DeviceType.RemoteIo]: 'Remote IO',
      [IntelligenceModels.DeviceList.DeviceType.ExternalStorage]:
        'External storage',
      [IntelligenceModels.DeviceList.DeviceType.AccessControl]:
        'Access control',
    };
    return labels[device.type];
  }

  public deviceImage(device: Device): string {
    const images: Partial<
      Record<IntelligenceModels.DeviceList.DeviceType, string>
    > = {
      [IntelligenceModels.DeviceList.DeviceType.Core]: 'assets/devices/core.png',
      [IntelligenceModels.DeviceList.DeviceType.AppleTv]:
        'assets/devices/apple_tv.png',
      [IntelligenceModels.DeviceList.DeviceType.Door]: 'assets/devices/door.png',
      [IntelligenceModels.DeviceList.DeviceType.Speaker]:
        'assets/devices/speaker.png',
      [IntelligenceModels.DeviceList.DeviceType.Sensor]:
        'assets/devices/sensor.png',
      [IntelligenceModels.DeviceList.DeviceType.RemoteIo]:
        'assets/devices/ios_remote.png',
      [IntelligenceModels.DeviceList.DeviceType.ExternalStorage]:
        'assets/devices/external_storage.png',
      [IntelligenceModels.DeviceList.DeviceType.AccessControl]:
        'assets/devices/physical_access_control.png',
    };
    return images[device.type] ?? '';
  }

  public statusIconClass(device: Device): string {
    if (device.status === 'online') return 'ui-icon-status-online';
    if (device.status === 'unhealthy') return 'ui-icon-status-unhealthy';
    if (device.status === 'offline') return 'ui-icon-status-offline';
    return '';
  }

  public coreName(device: Device): string {
    if (!device.edgeId) return '';
    return (
      this.allDevices().find(
        (candidate) =>
          candidate.type === IntelligenceModels.DeviceList.DeviceType.Core &&
          candidate.id === device.edgeId,
      )?.name ?? device.edgeId
    );
  }

  public testSelected(): void {
    const cameras = this.selectedCameras();
    const data: TestSelectedCameraModalData = {
      cameraIds: cameras.map((camera) => camera.cameraId),
      edgeIds: [...new Set(cameras.map((camera) => camera.edgeId))],
    };
    this.dialog.open(TestSelectedCameraModalComponent, {
      panelClass: 'modal-no-padding',
      height: '80vh',
      width: '85vw',
      data,
    });
  }

  public manageSelected(): void {
    this.dialog.open(BulkUpdateFormComponent, {
      panelClass: 'modal-no-padding',
      data: { camerasToManage: this.selectedCameras() },
    });
  }

  public changePasswordSelected(): void {
    this.dialog.open(ChangePasswordDialogComponent, {
      panelClass: 'modal-no-padding',
      data: { cameras: this.selectedCameras() },
    });
  }

  private updateCameraSelection(cameraIds: string[], checked: boolean): void {
    this.selectedCameraIds.update((current) => {
      const next = new Set(current);
      for (const cameraId of cameraIds) {
        if (checked) next.add(cameraId);
        else next.delete(cameraId);
      }
      return next;
    });
  }

  private locationDevices(location: DeviceLocation): Device[] {
    return [
      ...location.devices,
      ...location.subLocations.flatMap((subLocation) =>
        this.locationDevices(subLocation),
      ),
    ];
  }

  private allDevices(): Device[] {
    return this.locations().flatMap((location) =>
      this.locationDevices(location),
    );
  }
}
